/* ============================================
   ACCESSIBILITY JAVASCRIPT
   WCAG 2.1 AA Compliance
   ============================================ */

(function() {
  'use strict';

  const STORAGE_PREFIX = 'ww1991_a11y_';

  function announce(message, priority = 'polite') {
    const announcer = document.getElementById('a11y-announcer');
    if (!announcer) return;
    announcer.setAttribute('aria-live', priority);
    announcer.textContent = '';
    setTimeout(() => { announcer.textContent = message; }, 100);
  }

  function savePreference(key, value) {
    try { localStorage.setItem(STORAGE_PREFIX + key, value); } catch(e) {}
  }

  function loadPreference(key) {
    try { return localStorage.getItem(STORAGE_PREFIX + key); } catch(e) { return null; }
  }

  /* ─ Widget open/close ─ */
  function initA11yWidget() {
    const toggle = document.getElementById('a11y-toggle');
    const widget = document.getElementById('a11y-widget');
    const close  = document.getElementById('a11y-close');
    if (!toggle || !widget) return;

    function openWidget() {
      widget.classList.add('open');
      widget.setAttribute('aria-hidden', 'false');
      toggle.setAttribute('aria-expanded', 'true');
      announce('Accessibility options opened');
    }
    function closeWidget() {
      widget.classList.remove('open');
      widget.setAttribute('aria-hidden', 'true');
      toggle.setAttribute('aria-expanded', 'false');
      toggle.focus();
      announce('Accessibility options closed');
    }

    toggle.addEventListener('click', () => widget.classList.contains('open') ? closeWidget() : openWidget());
    close.addEventListener('click', closeWidget);
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && widget.classList.contains('open')) closeWidget(); });
    widget.addEventListener('keydown', (e) => {
      if (e.key === 'Tab' && widget.classList.contains('open')) {
        const focusable = widget.querySelectorAll('button:not([disabled]), a[href]');
        const first = focusable[0]; const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    });
    widget.addEventListener('click', (e) => e.stopPropagation());
  }

  /* ─ Font Size — applied to <html> so ALL rem-based sizes scale ─ */
  function initFontSize() {
    let currentSize = loadPreference('fontSize') || 'normal';
    applyFontSize(currentSize);
    updateFontBtns(currentSize);

    document.querySelectorAll('[data-action^="font-"]').forEach(btn => {
      btn.addEventListener('click', function(e) {
        e.preventDefault(); e.stopPropagation();
        const action = this.dataset.action;
        if (action === 'font-increase')  currentSize = currentSize === 'normal' ? 'large' : 'xlarge';
        else if (action === 'font-decrease') currentSize = currentSize === 'xlarge' ? 'large' : 'normal';
        else if (action === 'font-reset')    currentSize = 'normal';
        applyFontSize(currentSize);
        updateFontBtns(currentSize);
        savePreference('fontSize', currentSize);
        announce(`Font size: ${currentSize}`);
      });
    });
  }

  function applyFontSize(size) {
    /* Apply to <html> so rem cascade affects the ENTIRE page */
    document.documentElement.classList.remove('a11y-font-large', 'a11y-font-xlarge');
    if (size === 'large')  document.documentElement.classList.add('a11y-font-large');
    if (size === 'xlarge') document.documentElement.classList.add('a11y-font-xlarge');
  }

  function updateFontBtns(size) {
    const decreaseBtn = document.querySelector('[data-action="font-decrease"]');
    const resetBtn    = document.querySelector('[data-action="font-reset"]');
    const increaseBtn = document.querySelector('[data-action="font-increase"]');
    if (decreaseBtn) decreaseBtn.setAttribute('aria-pressed', size !== 'normal' ? 'true' : 'false');
    if (resetBtn)    resetBtn.setAttribute('aria-pressed', size === 'normal' ? 'true' : 'false');
    if (increaseBtn) increaseBtn.setAttribute('aria-pressed', size !== 'normal' ? 'true' : 'false');
  }

  /* ─ Toggle features ─ */
  function initToggleFeature(action, bodyClass, htmlClass, label) {
    const isEnabled = loadPreference(action) === 'true';
    const btn = document.querySelector(`[data-action="${action}"]`);
    if (isEnabled) {
      if (bodyClass) document.body.classList.add(bodyClass);
      if (htmlClass) document.documentElement.classList.add(htmlClass);
      if (btn) btn.setAttribute('aria-pressed', 'true');
    }
    if (btn) {
      btn.addEventListener('click', function(e) {
        e.preventDefault(); e.stopPropagation();
        let enabled;
        if (bodyClass) enabled = document.body.classList.toggle(bodyClass);
        if (htmlClass) { document.documentElement.classList.toggle(htmlClass); enabled = document.documentElement.classList.contains(htmlClass); }
        this.setAttribute('aria-pressed', enabled.toString());
        savePreference(action, enabled.toString());
        announce(`${label} ${enabled ? 'enabled' : 'disabled'}`);
      });
    }
  }

  function initHighContrast()  { initToggleFeature('high-contrast',  'a11y-high-contrast',  null,                   'High contrast mode'); }
  function initDarkMode()      { initToggleFeature('dark-mode',       'a11y-dark-mode',       null,                   'Inverted colors'); }
  function initReadableFont()  { initToggleFeature('readable-font',   null,                  'a11y-readable-font',   'Readable font'); }
  function initFocusMode()     { initToggleFeature('focus-mode',      'a11y-focus-mode',      null,                   'Focus highlights'); }

  /* ─ Reset All ─ */
  function initResetAll() {
    const resetBtn = document.querySelector('[data-action="reset-all"]');
    if (!resetBtn) return;
    resetBtn.addEventListener('click', (e) => {
      e.preventDefault(); e.stopPropagation();
      document.body.classList.remove('a11y-high-contrast', 'a11y-dark-mode', 'a11y-focus-mode', 'a11y-font-large', 'a11y-font-xlarge');
      document.documentElement.classList.remove('a11y-font-large', 'a11y-font-xlarge', 'a11y-readable-font');
      document.querySelectorAll('.a11y-btn[aria-pressed]').forEach(btn => btn.setAttribute('aria-pressed', 'false'));
      try {
        Object.keys(localStorage).forEach(key => {
          if (key.startsWith(STORAGE_PREFIX)) localStorage.removeItem(key);
        });
      } catch(e) {}
      announce('All accessibility settings reset');
    });
  }

  /* ─ Keyboard detection ─ */
  function initKeyboardDetection() {
    let isKeyboardUser = false;
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Tab') { isKeyboardUser = true; document.body.classList.add('keyboard-user'); }
    });
    document.addEventListener('mousedown', () => {
      if (isKeyboardUser) { isKeyboardUser = false; document.body.classList.remove('keyboard-user'); }
    });
  }

  /* ─ Mobile menu accessibility ─ */
  function enhanceMobileMenu() {
    const hamburger  = document.querySelector('.nav-hamburger');
    const mobileMenu = document.getElementById('mobile-menu');
    if (!hamburger || !mobileMenu) return;
    hamburger.addEventListener('click', function() {
      setTimeout(() => {
        const isOpen = mobileMenu.classList.contains('open');
        hamburger.setAttribute('aria-expanded', isOpen.toString());
        mobileMenu.setAttribute('aria-hidden', (!isOpen).toString());
        if (isOpen) {
          const firstLink = mobileMenu.querySelector('.mobile-menu__link');
          if (firstLink) setTimeout(() => firstLink.focus(), 100);
          announce('Mobile menu opened');
        } else { announce('Mobile menu closed'); }
      }, 50);
    });
  }

  /* ─ Cart drawer accessibility ─ */
  function enhanceCartDrawer() {
    const cartDrawer  = document.getElementById('cart-drawer');
    const cartTriggers = document.querySelectorAll('[data-cart-trigger]');
    const cartClose   = document.getElementById('cart-close');
    const cartOverlay = document.getElementById('cart-overlay');
    if (!cartDrawer) return;
    function updateCartAria(isOpen) {
      cartDrawer.setAttribute('aria-hidden', (!isOpen).toString());
      if (isOpen) { cartClose?.focus(); announce('Shopping cart opened'); }
      else announce('Shopping cart closed');
    }
    cartTriggers.forEach(t => t.addEventListener('click', () => setTimeout(() => updateCartAria(cartDrawer.classList.contains('open')), 50)));
    cartClose?.addEventListener('click', () => { setTimeout(() => updateCartAria(false), 50); cartTriggers[0]?.focus(); });
    cartOverlay?.addEventListener('click', () => { setTimeout(() => updateCartAria(false), 50); cartTriggers[0]?.focus(); });
    cartDrawer.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && cartDrawer.classList.contains('open')) cartClose?.click();
      if (e.key === 'Tab' && cartDrawer.classList.contains('open')) {
        const focusable = cartDrawer.querySelectorAll('button:not([disabled]), a[href], input:not([disabled])');
        const first = focusable[0]; const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    });
  }

  /* ─ Skip links ─ */
  function initSkipLinks() {
    document.querySelectorAll('.skip-to-content').forEach(link => {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        const target = document.querySelector(link.getAttribute('href'));
        if (target) { target.focus(); target.scrollIntoView({ behavior: 'smooth', block: 'start' }); }
      });
    });
  }

  /* ─ Form validation announcements ─ */
  function enhanceFormValidation() {
    document.querySelectorAll('input, textarea').forEach(field => {
      field.addEventListener('invalid', (e) => {
        e.preventDefault();
        const label = document.querySelector(`label[for="${field.id}"]`)?.textContent || 'This field';
        announce(`${label}: ${field.validationMessage}`, 'assertive');
      });
    });
  }

  /* ─ Boot ─ */
  document.addEventListener('DOMContentLoaded', () => {
    initA11yWidget();
    initFontSize();
    initHighContrast();
    initDarkMode();
    initReadableFont();
    initFocusMode();
    initResetAll();
    initKeyboardDetection();
    enhanceMobileMenu();
    enhanceCartDrawer();
    initSkipLinks();
    enhanceFormValidation();
    setTimeout(() => announce('Page loaded. Press Tab to navigate.'), 1000);
  });

  if (window.matchMedia) {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches)
      document.body.classList.add('prefers-reduced-motion');
    if (window.matchMedia('(prefers-contrast: high)').matches && !loadPreference('high-contrast'))
      document.body.classList.add('a11y-high-contrast');
  }

})();
